const LIST_CONNECTIONS_NOT_FOUND_RETENTION_MS = 5 * 60 * 1000;

export const createGraphApiSupport = ({
    graphQLRequest,
    escapeGraphQLString,
    serializeProperties,
    runMutation,
    logger,
    fetchGraphFilterLabels
} = {}) => {
    const listConnectionsNotFoundCache = new Map();
    const listConnectionsResponseCache = new Map();
    const listConnectionsInFlightCache = new Map();

    const buildListConnectionsKey = (entityId, entityType) => `${entityType}:${entityId}`;

    const isNotFoundListConnectionsError = (error) => {
        const message = String(error?.message || '').toLowerCase();
        return message.includes('not found');
    };

    const getRecentNotFoundListConnectionsError = (entityId, entityType) => {
        const key = buildListConnectionsKey(entityId, entityType);
        const cached = listConnectionsNotFoundCache.get(key);
        if (!cached?.failedAt) {
            return null;
        }

        const isFresh = Date.now() - cached.failedAt < LIST_CONNECTIONS_NOT_FOUND_RETENTION_MS;
        if (!isFresh) {
            listConnectionsNotFoundCache.delete(key);
            return null;
        }

        return new Error(cached.message || 'Entity not found');
    };

    const cacheNotFoundListConnectionsError = (entityId, entityType, error) => {
        if (!isNotFoundListConnectionsError(error)) {
            return;
        }

        const key = buildListConnectionsKey(entityId, entityType);
        listConnectionsNotFoundCache.set(key, {
            failedAt: Date.now(),
            message: error?.message || 'Entity not found'
        });
    };

    const clearCachedListConnectionsError = (entityId, entityType) => {
        const key = buildListConnectionsKey(entityId, entityType);
        listConnectionsNotFoundCache.delete(key);
    };

    const getRecentListConnectionsData = (entityId, entityType) => {
        const key = buildListConnectionsKey(entityId, entityType);
        const cached = listConnectionsResponseCache.get(key);
        if (!cached?.cachedAt) {
            return null;
        }

        const isFresh = Date.now() - cached.cachedAt < LIST_CONNECTIONS_NOT_FOUND_RETENTION_MS;
        if (!isFresh) {
            listConnectionsResponseCache.delete(key);
            return null;
        }

        try {
            return cached.data == null ? null : JSON.parse(JSON.stringify(cached.data));
        } catch {
            return cached.data;
        }
    };

    const cacheListConnectionsData = (entityId, entityType, data) => {
        const key = buildListConnectionsKey(entityId, entityType);
        listConnectionsResponseCache.set(key, {
            cachedAt: Date.now(),
            data
        });
    };

    const fetchTransactionRequests = async ({ requestedBy, limit = 100, page = 0 } = {}) => {
        const safeLimit = Math.max(1, Math.min(Number(limit) || 100, 100));
        const safePage = Math.max(0, Number(page) || 0);
        const offset = safePage * safeLimit;

        const filterParts = [`limit: ${safeLimit}`, `offset: ${offset}`];
        if (requestedBy) {
            filterParts.push(`requested_by: "${escapeGraphQLString(requestedBy)}"`);
        }

        const filterInput = filterParts.join(', ');
        const query = `query TransactionRequests {
      getTransactionRequests(filter: {${filterInput}}) {
        transaction_id
        entity_type
        entity_id
        action_type
        created_at
        req_payload
        requested_by
        status
        updated_at
        transaction_comment
        approval_comments
      }
    }`;

        const data = await graphQLRequest(query);
        const requests = data?.getTransactionRequests || [];

        return requests.map((item = {}) => {
            const entityType = item.entity_type?.toLowerCase?.() || '';
            const derivedNodeId = entityType === 'node' ? item.entity_id : null;
            const derivedEdgeId = entityType === 'edge' ? item.entity_id : null;

            return {
                ...item,
                node_id: item.node_id ?? derivedNodeId ?? null,
                edge_id: item.edge_id ?? derivedEdgeId ?? null
            };
        });
    };

    const formatConnectionTypeLabel = (value = '') => {
        const spaced = value.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
        if (!spaced) {
            return value;
        }

        return spaced
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const fetchConnectionTypes = async () => {
        try {
            const labels = await fetchGraphFilterLabels({ type: 'edge' });
            if (!labels.length) {
                return [];
            }

            const optionMap = new Map();
            labels.forEach((rawLabel) => {
                const value = rawLabel;
                if (!value || optionMap.has(value)) {
                    return;
                }
                optionMap.set(value, {
                    label: formatConnectionTypeLabel(value),
                    value
                });
            });

            return Array.from(optionMap.values());
        } catch (error) {
            logger.error('fetchConnectionTypes failed:', error);
            return [];
        }
    };

    const updateTransactionRequest = async ({ transactionId, properties, updatedBy, status, comments }) => {
        if (!transactionId) {
            throw new Error('Transaction ID is required');
        }

        let sanitizedProperties = properties;
        if (properties != null) {
            if (typeof properties === 'string') {
                try {
                    const parsed = JSON.parse(properties);
                    if (parsed && typeof parsed === 'object') {
                        if (status === undefined) {
                            delete parsed.status;
                        }
                        sanitizedProperties = parsed;
                    } else {
                        sanitizedProperties = properties;
                    }
                } catch {
                    sanitizedProperties = properties;
                }
            } else if (typeof properties === 'object') {
                sanitizedProperties = { ...properties };
                if (status === undefined) {
                    if (Object.hasOwn && Object.hasOwn(sanitizedProperties, 'status')) {
                        delete sanitizedProperties.status;
                    } else if (Object.prototype && Object.prototype.hasOwnProperty && Object.prototype.hasOwnProperty.call(sanitizedProperties, 'status')) {
                        delete sanitizedProperties.status;
                    }
                }
            }
        }

        const serializedProperties = serializeProperties(sanitizedProperties);
        const updatedByInput = updatedBy ? `\n        updated_by: "${escapeGraphQLString(updatedBy)}"` : '';
        const statusInput = typeof status === 'string' && status ? `\n        status: "${escapeGraphQLString(status)}"` : '';
        const commentsInput = typeof comments === 'string' && comments ? `\n        comments: "${escapeGraphQLString(comments)}"` : '';

        const query = `
      mutation UpdateTransactionRequest {
        updateTransactionRequest(input: {
          transaction_id: "${escapeGraphQLString(transactionId)}"
          properties: "${serializedProperties}"${updatedByInput}${statusInput}${commentsInput}
        }) {
          success
          message
          updatedCount
        }
      }
    `;

        return runMutation({
            query,
            entity: 'updateTransactionRequest',
            actionLabel: 'Update transaction request'
        });
    };

    const fetchAuditLog = async ({ limit = 20, offset = 0, itemId = null, actionBy = null } = {}) => {
        try {
            const safeLimit = Math.max(1, Math.min(100, limit));
            const safeOffset = Math.max(0, offset || 0);
            const itemFilter = typeof itemId === 'string' && itemId ? `, item_id: "${escapeGraphQLString(itemId)}"` : '';
            const actionByFilter = typeof actionBy === 'string' && actionBy ? `, action_by: "${escapeGraphQLString(actionBy)}"` : '';

            const query = `query GetAuditLogs { getAuditLogs(filter: { limit: ${safeLimit}, offset: ${safeOffset}${itemFilter}${actionByFilter} }) { items { action action_by approval_by approval_comments approval_id logTime name status properties item_id transaction_comments transaction_id type } pagination { limit offset } } }`;
            const data = await graphQLRequest(query);

            let items = [];
            let total = null;
            let pagination = null;

            const result = data?.getAuditLogs;
            if (Array.isArray(result)) {
                items = result;
                total = null;
            } else if (result && Array.isArray(result.items)) {
                items = result.items;
                total = typeof result.total === 'number' ? result.total : null;
                pagination = result.pagination || null;
            }

            return { items, total, pagination };
        } catch (error) {
            logger.warn('fetchAuditLog failed, returning empty list:', error?.message || error);
            return { items: [], total: null };
        }
    };

    const fetchListOfConnections = async ({ entityId, entityType = 'node', bypassCache = false }) => {
        if (!entityId) {
            throw new Error('Entity ID is required');
        }

        const validTypes = ['node', 'edge'];
        if (!validTypes.includes(entityType)) {
            throw new Error('Entity type must be "node" or "edge"');
        }

        if (!bypassCache) {
            const retainedError = getRecentNotFoundListConnectionsError(entityId, entityType);
            if (retainedError) {
                throw retainedError;
            }

            const retainedData = getRecentListConnectionsData(entityId, entityType);
            if (retainedData) {
                return retainedData;
            }
        }

        const requestKey = buildListConnectionsKey(entityId, entityType);
        if (!bypassCache) {
            const inFlightRequest = listConnectionsInFlightCache.get(requestKey);
            if (inFlightRequest) {
                return inFlightRequest;
            }
        }

        const query = `
      query GetListOfConnections {
        getListOfConnections(input: {
          entityId: "${escapeGraphQLString(entityId)}"
          entityType: ${entityType}
        }) {
          error
          data {
            id
            type
            properties
            edges {
              id
              label
              properties
              source
              target
            }
            source
            target
          }
        }
      }
    `;

        const requestPromise = (async () => {
            try {
                const data = await graphQLRequest(query);
                const result = data?.getListOfConnections;

                if (result?.error) {
                    const apiError = new Error(result.error);
                    cacheNotFoundListConnectionsError(entityId, entityType, apiError);
                    throw apiError;
                }

                const responseData = result?.data || null;

                if (responseData) {
                    if (responseData.source && typeof responseData.source === 'string') {
                        try {
                            responseData.source = JSON.parse(responseData.source);
                        } catch {
                            // Keep as-is when parsing fails.
                        }
                    }
                    if (responseData.target && typeof responseData.target === 'string') {
                        try {
                            responseData.target = JSON.parse(responseData.target);
                        } catch {
                            // Keep as-is when parsing fails.
                        }
                    }

                    if (Array.isArray(responseData.edges)) {
                        responseData.edges = responseData.edges.map((edge) => {
                            const parsedEdge = { ...edge };

                            if (edge.source && typeof edge.source === 'string') {
                                try {
                                    const parsed = JSON.parse(edge.source);
                                    parsedEdge.source = parsed;
                                    parsedEdge.sourceNode = typeof parsed === 'object' ? parsed : null;
                                    parsedEdge.isSourceSelf = parsed === 'self';
                                } catch {
                                    parsedEdge.source = edge.source;
                                }
                            }

                            if (edge.target && typeof edge.target === 'string') {
                                try {
                                    const parsed = JSON.parse(edge.target);
                                    parsedEdge.target = parsed;
                                    parsedEdge.targetNode = typeof parsed === 'object' ? parsed : null;
                                    parsedEdge.isTargetSelf = parsed === 'self';
                                } catch {
                                    parsedEdge.target = edge.target;
                                }
                            }

                            return parsedEdge;
                        });
                    }
                }

                clearCachedListConnectionsError(entityId, entityType);
                cacheListConnectionsData(entityId, entityType, responseData);
                return responseData;
            } catch (error) {
                cacheNotFoundListConnectionsError(entityId, entityType, error);
                logger.error('fetchListOfConnections failed:', error, 'entityId, entityType:', entityId, entityType);
                throw error;
            }
        })();

        if (bypassCache) {
            return requestPromise;
        }

        listConnectionsInFlightCache.set(requestKey, requestPromise);
        try {
            return await requestPromise;
        } finally {
            listConnectionsInFlightCache.delete(requestKey);
        }
    };

    const submitForApproval = async ({ transactionIds, updatedBy }) => {
        if (!Array.isArray(transactionIds) || transactionIds.length === 0) {
            throw new Error('No transaction IDs provided for approval.');
        }

        const idsString = transactionIds.map((id) => `"${escapeGraphQLString(id)}"`).join(', ');
        const updatedByInput = updatedBy ? `, updatedBy: "${escapeGraphQLString(updatedBy)}"` : '';

        const query = `
      mutation SubmitForApproval {
        submitForApproval(input: { transactionIds: [${idsString}]${updatedByInput} }) {
          success
          message
          approvedCount
          rejectedCount
        }
      }
    `;

        return runMutation({
            query,
            entity: 'submitForApproval',
            actionLabel: 'Submit for Approval'
        });
    };

    const updateApprovalRequest = async ({ approvalIds, status, comments, approvedBy }) => {
        const ids = Array.isArray(approvalIds) ? approvalIds : (approvalIds ? [approvalIds] : []);
        if (!ids.length) {
            throw new Error('No approval ID(s) provided.');
        }

        const idsArrayStr = `[${ids.map((id) => `"${escapeGraphQLString(id)}"`).join(', ')}]`;
        const inputParts = [`approval_ids: ${idsArrayStr}`];

        if (status) {
            inputParts.push(`status: "${escapeGraphQLString(status.toUpperCase())}"`);
        }
        if (comments !== undefined) {
            inputParts.push(`comments: "${escapeGraphQLString(comments)}"`);
        }
        if (approvedBy) {
            inputParts.push(`approved_by: "${escapeGraphQLString(approvedBy)}"`);
        }

        const query = `
      mutation UpdateApprovalRequest {
        updateApprovalRequest(input: { ${inputParts.join(', ')} }) {
          success
          message
          updatedCount
        }
      }
    `;

        return runMutation({
            query,
            entity: 'updateApprovalRequest',
            actionLabel: 'Update Approval Request'
        });
    };

    const fetchApprovalRequests = async ({ limit = 50, offset = 0, approvalId = null, requestedBy = null } = {}) => {
        const filterParts = [];
        filterParts.push(`limit: ${Math.min(Math.max(limit, 1), 100)}`);
        filterParts.push(`offset: ${Math.max(offset, 0)}`);

        if (approvalId) {
            filterParts.push(`approval_id: "${escapeGraphQLString(approvalId)}"`);
        }

        if (requestedBy) {
            filterParts.push(`requested_by: "${escapeGraphQLString(requestedBy)}"`);
        }

        const filterString = filterParts.join(', ');

        const query = `
      query GetApprovalRequests {
        getApprovalRequest(filter: { ${filterString} }) {
          items {
            is_neptune_updated
            neptune_updated_time
            action_type
            approval_comments
            approval_created_at
            approval_id
            approval_status
            approval_updated_at
            approved_by
            entity_id
            entity_type
            req_payload
            requested_by
            transaction_comments
            transaction_created_at
            transaction_id
            transaction_updated_at
          }
        }
      }
    `;

        try {
            const data = await graphQLRequest(query);
            return data?.getApprovalRequest || { items: [] };
        } catch (error) {
            logger.error('Failed to fetch approval requests:', error);
            throw error;
        }
    };

    const fetchTrackStatusRequests = async ({ offset = 0, requestedBy = null } = {}) => {
        const safeLimit = 20;
        const safeOffset = Math.max(offset, 0);

        const filterParts = [];
        filterParts.push(`limit: ${safeLimit}`);
        filterParts.push(`offset: ${safeOffset}`);

        if (requestedBy) {
            filterParts.push(`requested_by: "${escapeGraphQLString(requestedBy)}"`);
        }

        const filterString = filterParts.join(', ');

        const query = `
      query GetTrackApproval {
        getTrackApproval(filter: { ${filterString} }) {
          items {
            approval_time
            approved_by
            status
            transaction_comments
            approval_comments
            action_type
            entity_id
            entity_type
            name
            request_time
            requested_by
            transaction_id
          }
          pagination {
            limit
            offset
          }
        }
      }
    `;

        try {
            const data = await graphQLRequest(query);
            return data?.getTrackApproval || { items: [] };
        } catch (error) {
            logger.error('Failed to fetch track status requests:', error);
            throw error;
        }
    };

    const fetchAllProperties = async () => {
        const query = `query MyQuery { listAllProperties { properties {key values} } }`;
        try {
            const data = await graphQLRequest(query);
            const props = data?.listAllProperties?.properties;

            const toSampleString = (value) => {
                if (value === null || value === undefined) {
                    return null;
                }
                if (typeof value === 'string') {
                    return value;
                }
                if (typeof value === 'number' || typeof value === 'boolean') {
                    return String(value);
                }
                try {
                    return JSON.stringify(value);
                } catch {
                    return String(value);
                }
            };

            const normalizeSample = (value) => {
                const raw = toSampleString(value);
                if (raw == null) {
                    return null;
                }
                const trimmed = String(raw).trim();
                if (!trimmed) {
                    return null;
                }
                const lower = trimmed.toLowerCase();
                if (lower === 'nan' || lower === 'undefined' || lower === 'null') {
                    return null;
                }
                return trimmed;
            };

            const pickSampleFromValues = (values) => {
                if (!Array.isArray(values) || values.length === 0) {
                    return null;
                }

                const firstNormalized = normalizeSample(values[0]);
                if (firstNormalized) {
                    return firstNormalized;
                }

                let best = null;
                for (let i = 0; i < values.length; i += 1) {
                    const candidate = normalizeSample(values[i]);
                    if (!candidate) {
                        continue;
                    }
                    if (best == null || candidate.length < best.length) {
                        best = candidate;
                    }
                }
                return best;
            };

            const results = [];
            const seen = new Set();

            const push = (keyRaw, sampleRaw) => {
                const key = typeof keyRaw === 'string' ? keyRaw.trim() : String(keyRaw || '').trim();
                if (!key || seen.has(key)) {
                    return;
                }

                seen.add(key);
                results.push({ key, sampleValue: normalizeSample(sampleRaw) });
            };

            if (Array.isArray(props)) {
                props.forEach((item) => {
                    if (typeof item === 'string') {
                        push(item, null);
                        return;
                    }
                    if (item && typeof item === 'object') {
                        const key = item.key ?? item.name ?? item.property ?? item.propertyKey ?? item.value ?? item.label;

                        if (Array.isArray(item.values)) {
                            push(key, pickSampleFromValues(item.values));
                            return;
                        }

                        const sample = item.sampleValue ?? item.sample_value ?? item.sampleData ?? item.sample_data ?? item.sample ?? item.example;
                        if (key) {
                            push(key, sample);
                        }
                        return;
                    }
                    if (item !== null && item !== undefined) {
                        push(item, null);
                    }
                });
            } else if (props && typeof props === 'object') {
                Object.entries(props).forEach(([key, sample]) => push(key, sample));
            }

            return results;
        } catch (error) {
            logger.warn('fetchAllProperties failed:', error?.message || error);
            return [];
        }
    };

    const fetchGeoMapDataset = async () => {
        const query = `query GeoMapDataset {
      geoMapDataset {
        generatedAt
        plants
        ports
        vdcs
        regions
        hqs
        error
        lexusSeriesCount
        toyotaSeriesCount
      }
    }`;

        const data = await graphQLRequest(query);
        const payload = data?.geoMapDataset;

        if (!payload) {
            throw new Error('GeoMap dataset response is empty.');
        }

        if (payload?.error) {
            throw new Error(payload.error);
        }

        return payload;
    };

    const fetchSecretValue = async (secretName) => {
        const safeSecretName = escapeGraphQLString(secretName || '');
        if (!safeSecretName.trim()) {
            throw new Error('Secret name is required to fetch runtime map config.');
        }

        const query = `query GetSecretValue {
      getSecretValue(secret_name: "${safeSecretName}") {
        secretValue
        error
      }
    }`;

        const data = await graphQLRequest(query);
        const payload = data?.getSecretValue;

        if (!payload) {
            throw new Error('Secret response is empty.');
        }

        if (payload.error) {
            throw new Error(payload.error);
        }

        return payload;
    };

    return {
        fetchTransactionRequests,
        fetchConnectionTypes,
        updateTransactionRequest,
        fetchAuditLog,
        fetchListOfConnections,
        submitForApproval,
        updateApprovalRequest,
        fetchApprovalRequests,
        fetchTrackStatusRequests,
        fetchAllProperties,
        fetchGeoMapDataset,
        fetchSecretValue
    };
};